import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { MdLibraryBooks, MdNotificationsActive } from "react-icons/md";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import ComoUtilizarModal from "@/components/consignado/Cobranca/VinculosAtivosRedesign/ComoUtilizarModal";
import Pagination from "@/components/consignado/Cobranca/VinculosAtivosRedesign/Pagination";
import {
  CatalogoDisparo,
  ContextoDisparo,
  DisparoListItem,
  DisparosTotais,
  TipoGatilho,
} from "@/models/consignado/disparo.model";
import DisparoTable from "./DisparoTable";
import DisparosFiltersBar from "./DisparosFiltersBar";
import { CONCEITOS_DISPARO, PASSOS_LISTAGEM } from "./comoUtilizar.content";

export type DisparosFiltros = {
  ativo?: boolean;
  contexto?: ContextoDisparo;
  tipoGatilho?: TipoGatilho;
};

/**
 * Contagens da faixa de tags acima da tabela — as do redesign anterior
 * (index.tsx da feat/storybook), com o vocabulário de hoje: "Cronjobs" virou
 * "Rotinas" e entrou "Manuais", que é o terceiro gatilho do cadastro.
 *
 * Continua opcional: a grade é paginada no servidor e o `GET /disparos`
 * devolve só `total`, então contar por gatilho a partir da página atual daria
 * número errado. Quem preenche é o `GET /disparos/resumo`, numa consulta
 * agregada — e enquanto ela não responde, as tags não aparecem.
 *
 * O tipo mora no model porque virou contrato de API, não formato de componente.
 */
export type { DisparosTotais } from "@/models/consignado/disparo.model";

/**
 * As três tags de gatilho também são o recorte da grade: `GET /disparos`
 * aceita `tipoGatilho`, então clicar na contagem mostra exatamente as linhas
 * que ela conta. Total e Ativos ficam de leitura — situação já tem os botões
 * do cabeçalho, e "todos" é o estado de nenhuma tag selecionada.
 */
const TAGS_GATILHO: {
  tipo: TipoGatilho;
  rotulo: string;
  colorScheme: string;
  contar: (t: DisparosTotais) => number;
}[] = [
  { tipo: TipoGatilho.EVENTO, rotulo: "Eventos", colorScheme: "blue", contar: (t) => t.eventos },
  { tipo: TipoGatilho.ROTINA, rotulo: "Rotinas", colorScheme: "purple", contar: (t) => t.rotinas },
  // "Manuais" saiu junto com o gatilho manual do formulário (decisão do tech lead).
];

type Props = {
  items: DisparoListItem[];
  total: number;
  /** Quando ausente, a faixa de tags não é renderizada. */
  totais?: DisparosTotais;
  pagina: number;
  limite: number;
  filtros: DisparosFiltros;
  catalogo?: CatalogoDisparo;
  isLoading?: boolean;
  isError?: boolean;
  togglingId?: string | null;
  onPaginaChange: (pagina: number) => void;
  onLimiteChange: (limite: number) => void;
  onFiltrosChange: (filtros: DisparosFiltros) => void;
  onNovo: () => void;
  /** Abre os templates reutilizáveis. Sem ele, o botão não aparece. */
  onTemplates?: () => void;
  onVer: (disparo: DisparoListItem) => void;
  onEditar: (disparo: DisparoListItem) => void;
  onExcluir: (disparo: DisparoListItem) => void;
  onToggleAtivo: (disparo: DisparoListItem) => void;
};

/**
 * Listagem de disparos — 100% controlada: quem chama decide de onde vêm os
 * itens (API ou fixtures) e o que cada ação faz.
 *
 * A paginação e os filtros são do servidor: `GET /disparos` aceita pagina,
 * limite, ativo e contexto, e devolve `total`. Os filtros ficam no cabeçalho,
 * como nas telas de Cobrança e Leilão, e o total vem do rodapé da paginação.
 */
export default function DisparosView({
  items,
  total,
  totais,
  pagina,
  limite,
  filtros,
  catalogo,
  isLoading,
  isError,
  togglingId,
  onPaginaChange,
  onLimiteChange,
  onFiltrosChange,
  onNovo,
  onTemplates,
  onVer,
  onEditar,
  onExcluir,
  onToggleAtivo,
}: Props) {
  const backgroundStyle = useConditionalBackground();
  const deleteDialog = useDisclosure();
  const [pendingDelete, setPendingDelete] = useState<DisparoListItem | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const totalPages = Math.max(1, Math.ceil(total / limite));

  // Clicar na tag já selecionada desfaz o recorte — é como o usuário volta
  // para "todos" sem procurar outro controle.
  const alternarGatilho = (tipo: TipoGatilho) =>
    onFiltrosChange({
      ...filtros,
      tipoGatilho: filtros.tipoGatilho === tipo ? undefined : tipo,
    });

  const requestDelete = (item: DisparoListItem) => {
    setPendingDelete(item);
    deleteDialog.onOpen();
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    onExcluir(pendingDelete);
    setPendingDelete(null);
    deleteDialog.onClose();
  };

  return (
    <Box w="100%" padding={{ base: "2", md: "4" }}>
      {/* Header */}
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: "4", md: "6" }}
        color="white"
        boxShadow="md"
        width="100%"
        mb={6}
      >
        <Flex
          align={{ base: "flex-start", md: "center" }}
          justify="space-between"
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Flex align="center">
            <MdNotificationsActive size="2rem" />
            <Box ml={3}>
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" fontFamily="Lexend Deca">
                Configurações de Notificações
              </Text>
              <Text fontSize="sm" opacity={0.85} fontFamily="Lexend Deca">
                Configuração de Disparos
              </Text>
            </Box>
          </Flex>

          <Flex align="center" gap={3}>
            <ComoUtilizarModal
              tituloTela="Configuração de Disparos"
              passos={PASSOS_LISTAGEM}
              conceitos={CONCEITOS_DISPARO}
            />
            {onTemplates && (
              <Button
                leftIcon={<MdLibraryBooks />}
                variant="outline"
                color="white"
                borderColor="whiteAlpha.700"
                _hover={{ bg: "whiteAlpha.300" }}
                onClick={onTemplates}
                fontFamily="Lexend Deca"
              >
                Templates
              </Button>
            )}
            <Button
              leftIcon={<AddIcon />}
              bg="white"
              color="primary"
              _hover={{ bg: "whiteAlpha.800" }}
              onClick={onNovo}
              fontFamily="Lexend Deca"
            >
              Novo disparo
            </Button>
          </Flex>
        </Flex>

        {/* Filtros no cabeçalho, como nas telas de Cobrança e Leilão. */}
        <Box mt={{ base: "4", md: "6" }}>
          <DisparosFiltersBar filtros={filtros} onChange={onFiltrosChange} catalogo={catalogo} />
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box w="100%" bg="white" borderRadius="xl" boxShadow="sm" p={{ base: 2, md: 4 }}>
        {isLoading ? (
          <Flex w="100%" justify="center" py={12}>
            <Spinner color="primary" />
          </Flex>
        ) : isError ? (
          <Flex w="100%" justify="center" py={12}>
            <Text color="red.500" fontFamily="Lexend Deca">
              Não foi possível carregar os disparos.
            </Text>
          </Flex>
        ) : (
          <>
            {/* Faixa de tags do redesign anterior: alinhada à direita, logo
                acima da tabela. Acompanha os filtros do cabeçalho — conta o
                mesmo conjunto que a paginação numera no rodapé. */}
            {totais && (
              <Flex justify="flex-end" gap={3} mb={4} wrap="wrap" fontFamily="Lexend Deca">
                <Tag colorScheme="gray" fontFamily="Lexend Deca">
                  Total: {totais.total}
                </Tag>
                <Tag colorScheme="green" fontFamily="Lexend Deca">
                  Ativos: {totais.ativos}
                </Tag>
                {TAGS_GATILHO.map(({ tipo, rotulo, colorScheme, contar }) => {
                  const selecionado = filtros.tipoGatilho === tipo;
                  return (
                    <Tooltip
                      key={tipo}
                      placement="top"
                      fontSize="md"
                      label={
                        selecionado
                          ? "Clique para remover o recorte"
                          : `Mostrar apenas disparos por ${rotulo.toLowerCase()}`
                      }
                    >
                      <Tag
                        as="button"
                        type="button"
                        colorScheme={colorScheme}
                        variant={selecionado ? "solid" : "subtle"}
                        fontFamily="Lexend Deca"
                        cursor="pointer"
                        onClick={() => alternarGatilho(tipo)}
                      >
                        {rotulo}: {contar(totais)}
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Flex>
            )}

            <DisparoTable
              disparos={items}
              catalogo={catalogo}
              togglingId={togglingId}
              onView={onVer}
              onEdit={onEditar}
              onDelete={requestDelete}
              onToggleAtivo={onToggleAtivo}
            />

            <Pagination
              currentPage={pagina}
              totalPages={totalPages}
              pageSize={limite}
              totalItems={total}
              onPageChange={onPaginaChange}
              onPageSizeChange={onLimiteChange}
              itemLabel="disparo"
              pageSizeOptions={[5, 10, 20, 50, 100]}
            />
          </>
        )}
      </Box>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent fontFamily="Lexend Deca">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir disparo
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir o disparo <strong>{pendingDelete?.nome}</strong>? Esta
              ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={deleteDialog.onClose} variant="ghost">
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmDelete}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
